import { Link } from "react-router-dom";
import { Nav } from "../components/Nav";
import { ArrowUpRight, Sparkles, Palette, Scissors, Wand2 } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/ff329c96b57c5d88f8d6918db89de582d90ed465b0a1966405b1b66cb653dcd2.png";

// Three editorial hero categories — each card features split her/him portraits
// so the visual story is unmistakably for both women AND men.
const SHOWCASE = [
  {
    id: "wedding",
    herImg: "https://images.unsplash.com/photo-1600685890506-593fdf55949b?w=900&q=80",
    himImg: "https://images.unsplash.com/photo-1760080839107-6798cee22d95?w=900&q=80",
    eyebrow: "For Weddings",
    title: "Bridal looks. For her & him.",
    body: "Silk saree, sindoor and gold for her. Sherwani, safa and embroidered kurta for him. Every ritual, every region.",
  },
  {
    id: "office",
    herImg: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&q=80",
    himImg: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=900&q=80",
    eyebrow: "For Work",
    title: "Office, owned.",
    body: "Sharp blazers for her. Tailored suits for him. Quiet luxury that walks into Monday morning with intent.",
  },
  {
    id: "party",
    herImg: "https://images.unsplash.com/photo-1765229279829-2d761e6cc816?w=900&q=80",
    himImg: "https://images.unsplash.com/photo-1665170338676-d30bc6e1f073?w=900&q=80",
    eyebrow: "For Evenings",
    title: "Party, undone.",
    body: "Velvet dresses and smokey eyes. Satin shirts and structured trousers. Every gathering, a moment.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0E13]/40 via-[#0F0E13]/70 to-[#0F0E13]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 sm:pt-32 pb-32 sm:pb-48">
          <div className="max-w-3xl fade-up">
            <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">An AI Atelier · For Him & Her</span>
            <h1 className="font-serif font-light text-5xl sm:text-7xl lg:text-8xl mt-8 leading-[0.95] tracking-tight">
              StyleMe.<br />
              <em className="text-[#E3C2B0] not-italic font-normal">For every</em><br />
              moment.
            </h1>
            <p className="mt-10 text-base sm:text-lg text-[#A8A6A1] max-w-xl leading-relaxed">
              Upload your photograph, name the occasion. In moments, receive a complete look —
              hand-curated outfit, hair, makeup and color palette — visualised on you.
            </p>
            <div className="mt-12 flex items-center gap-6 flex-wrap">
              <Link to="/auth?mode=register" data-testid="hero-cta-primary" className="group inline-flex items-center gap-3 bg-[#E3C2B0] text-[#0A0908] px-8 py-4 rounded-sm hover:bg-[#F0D4C5] transition-all">
                <span className="text-xs uppercase tracking-[0.3em] whitespace-nowrap">Begin with 3 Free Looks</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </Link>
              <Link to="/auth?mode=login" data-testid="hero-cta-secondary" className="text-xs uppercase tracking-[0.3em] text-[#F7F5F2] border-b border-[#A8A6A1]/40 hover:border-[#E3C2B0] pb-1 transition-colors">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SHOWCASE — 3 use cases at a glance */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 sm:py-32">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">01 · How you'll use StyleMe</span>
            <h2 className="font-serif text-4xl sm:text-6xl mt-4 font-light">One app · countless occasions.</h2>
          </div>
          <p className="max-w-md text-[#A8A6A1]">
            Whether it's your wedding, your first investor pitch or a Saturday cocktail party — StyleMe knows the codes.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {SHOWCASE.map((s, i) => (
            <div
              key={s.id}
              data-testid={`showcase-${s.id}`}
              className="relative aspect-[3/4] overflow-hidden rounded-sm group fade-up"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              {/* Split her/him portraits — proof the app is for both */}
              <div className="absolute inset-0 grid grid-cols-2">
                <div className="relative overflow-hidden">
                  <img
                    src={s.herImg}
                    alt={`${s.title} — for her`}
                    data-testid={`showcase-${s.id}-her`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2] bg-[#0F0E13]/60 backdrop-blur-sm px-2 py-1 rounded-sm">
                    Her
                  </span>
                </div>
                <div className="relative overflow-hidden border-l border-[#0F0E13]/30">
                  <img
                    src={s.himImg}
                    alt={`${s.title} — for him`}
                    data-testid={`showcase-${s.id}-him`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2] bg-[#0F0E13]/60 backdrop-blur-sm px-2 py-1 rounded-sm">
                    Him
                  </span>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E13] via-[#0F0E13]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 right-0">
                <div className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0] mb-3">{s.eyebrow}</div>
                <div className="font-serif text-3xl text-[#F7F5F2]">{s.title}</div>
                <p className="mt-3 text-sm text-[#F7F5F2]/80 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
          {["Date Night", "Festival", "Beach", "Formal", "Casual"].map((t, i) => (
            <div key={t} className="border border-white/10 rounded-sm py-6 text-center text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:border-[#E3C2B0] hover:text-[#F7F5F2] transition-all fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              {t}
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 sm:py-32">
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">02 · The Ritual</span>
          <h2 className="font-serif text-4xl sm:text-6xl mt-4 font-light max-w-3xl">Three quiet steps to a complete transformation.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 mt-20">
            <Step n="I" icon={<Sparkles strokeWidth={1} className="w-8 h-8" />} title="Upload or click" copy="Share a clear portrait — upload from your phone or capture live with your camera. Natural light is the secret ingredient." />
            <Step n="II" icon={<Wand2 strokeWidth={1} className="w-8 h-8" />} title="Whisper the moment" copy="Wedding mehendi at golden hour? Investor dinner in Tokyo? The more specific, the more magical." />
            <Step n="III" icon={<Palette strokeWidth={1} className="w-8 h-8" />} title="Receive your look" copy="A full editorial dossier — colors, fabrics, lip finishes, hair textures — visualised on you, in your language." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 sm:py-32 text-center">
          <Scissors className="w-8 h-8 text-[#E3C2B0] mx-auto mb-8" strokeWidth={1} />
          <h2 className="font-serif text-4xl sm:text-7xl font-light leading-tight">The mirror is ready.</h2>
          <p className="mt-6 text-[#A8A6A1] max-w-lg mx-auto">
            Join StyleMe. Your next unforgettable look is one photograph away.
          </p>
          <Link to="/auth?mode=register" data-testid="footer-cta" className="inline-flex items-center gap-3 mt-12 bg-[#E3C2B0] text-[#0A0908] px-10 py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors">
            <span className="text-xs uppercase tracking-[0.3em]">Create Free Account</span>
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="border-t border-white/5 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-8 items-start">
            <div className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">
              © StyleMe · An AI styling atelier · For him & her
            </div>
            <nav className="flex flex-wrap gap-x-8 gap-y-3 md:justify-end text-xs uppercase tracking-[0.3em]" data-testid="landing-footer-nav">
              <Link to="/about" className="text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors">About</Link>
              <Link to="/blog" className="text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors">Journal</Link>
              <Link to="/contact" className="text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors">Contact</Link>
              <Link to="/privacy" className="text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors">Privacy</Link>
              <Link to="/terms" className="text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors">Terms</Link>
              <Link to="/refund" className="text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors">Refund</Link>
            </nav>
          </div>
        </div>
      </section>
    </div>
  );
}

const Step = ({ n, icon, title, copy }) => (
  <div className="bg-[#0F0E13] p-10 group hover:bg-[#1A1921] transition-colors">
    <div className="flex items-center justify-between">
      <span className="font-serif text-xs uppercase tracking-[0.4em] text-[#A8A6A1]">Step {n}</span>
      <span className="text-[#E3C2B0]">{icon}</span>
    </div>
    <h3 className="font-serif text-3xl mt-12 font-light">{title}</h3>
    <p className="mt-4 text-sm text-[#A8A6A1] leading-relaxed">{copy}</p>
  </div>
);
